import { useMemo, useState } from 'react';
import { Search } from 'lucide-react';
import {
  Badge,
  Card,
  Input,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@design-system';
import type { FeatureFlagOp } from '@shared';
import { classifyFlagKeys } from '../utils/classify-flag-keys';
import { findAdvisorsWithOverride } from '../utils/resolve-advisor-flags';
import { isKnownFlagKey } from '../utils/known-flag-keys';
import { LegacyFlagWarningIcon } from './LegacyFlagWarningIcon';

type FlagCentricViewProps = {
  originalConfig: AppConfigSchema;
  targetConfig: AppConfigSchema;
  onEnqueueOp: (op: FeatureFlagOp) => void;
  isSaving?: boolean;
};

export function FlagCentricView({
  originalConfig,
  targetConfig,
  onEnqueueOp,
  isSaving,
}: FlagCentricViewProps) {
  const [filter, setFilter] = useState('');
  const { known, unknown } = useMemo(
    () => classifyFlagKeys(targetConfig),
    [targetConfig],
  );

  const rows = useMemo(() => {
    type Row = {
      flagKey: string;
      isKnown: boolean;
      targetValue: boolean | undefined;
      originalValue: boolean | undefined;
      overrideCount: number;
    };

    const build = (flagKey: string, keepUnset: boolean): Row | null => {
      const targetValue = (
        targetConfig.allAdvisors as Record<string, boolean | undefined> | undefined
      )?.[flagKey];
      if (typeof targetValue !== 'boolean' && !keepUnset) return null;
      const originalValue = (
        originalConfig.allAdvisors as Record<string, boolean | undefined> | undefined
      )?.[flagKey];
      return {
        flagKey,
        isKnown: isKnownFlagKey(flagKey),
        targetValue: typeof targetValue === 'boolean' ? targetValue : undefined,
        originalValue,
        overrideCount: findAdvisorsWithOverride(targetConfig, flagKey).length,
      };
    };

    const isRow = (r: Row | null): r is Row => r !== null;

    // Sort: on → off → unset, then by flag key.
    const rank = (v: boolean | undefined): number => (v === true ? 0 : v === false ? 1 : 2);
    const sortGroup = (items: Row[]): Row[] =>
      [...items].sort((a, b) => {
        const ra = rank(a.targetValue);
        const rb = rank(b.targetValue);
        if (ra !== rb) return ra - rb;
        return a.flagKey.localeCompare(b.flagKey);
      });

    return [
      ...sortGroup(known.map(key => build(key, true)).filter(isRow)),
      ...sortGroup(unknown.map(key => build(key, false)).filter(isRow)),
    ];
  }, [targetConfig, originalConfig, known, unknown]);

  const visibleRows = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(r => r.flagKey.toLowerCase().includes(q));
  }, [rows, filter]);

  return (
    <TooltipProvider delayDuration={150}>
      <div className="max-w-4xl space-y-3">
        <div className="flex items-center gap-3">
          <div className="relative max-w-md flex-1">
            <Search className="text-muted-foreground absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2" />
            <Input
              value={filter}
              onChange={e => setFilter(e.target.value)}
              placeholder="Filter flag keys..."
              className="pl-9"
            />
          </div>
          <div className="text-muted-foreground text-xs">
            {visibleRows.length} of {rows.length} flag keys
          </div>
        </div>

        <Card className="border border-gray-200 p-0">
          <Table className="[&_td]:px-5 [&_td]:py-4 [&_th]:px-5 [&_th]:py-3.5">
            <TableHeader>
              <TableRow>
                <TableHead className="w-[55%]">Flag</TableHead>
                <TableHead className="w-[25%]">All advisors</TableHead>
                <TableHead className="w-[20%] text-right">Overrides</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {visibleRows.map(row => {
                const isPending = row.targetValue !== row.originalValue;
                const switchChecked = row.targetValue === true;
                return (
                  <TableRow key={row.flagKey}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-xs">{row.flagKey}</span>
                        {!row.isKnown && <LegacyFlagWarningIcon />}
                        {isPending && (
                          <Badge className="bg-amber-100 text-[10px] text-amber-900 hover:bg-amber-100">
                            pending
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="inline-block">
                              <Switch
                                checked={switchChecked}
                                disabled={!row.isKnown || isSaving}
                                onCheckedChange={checked =>
                                  onEnqueueOp({
                                    type: 'SET_ALL_ADVISORS',
                                    flagKey: row.flagKey,
                                    value: checked,
                                  })
                                }
                              />
                            </span>
                          </TooltipTrigger>
                          <TooltipContent className="max-w-xs">
                            {!row.isKnown
                              ? 'Legacy flag — not in AppConfigFeatureFlagKey. Cannot edit.'
                              : row.targetValue === undefined
                                ? 'Not set in AppConfig yet. Toggle on to enable for every advisor by default.'
                                : row.targetValue
                                  ? 'Enabled for every advisor by default.'
                                  : 'Disabled by default. Individual advisors can still be opted in.'}
                          </TooltipContent>
                        </Tooltip>
                        <span className="text-muted-foreground text-xs">
                          {row.targetValue === undefined
                            ? 'Unset'
                            : row.targetValue
                              ? 'On'
                              : 'Off'}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <span
                        className={
                          row.overrideCount > 0
                            ? 'font-medium text-gray-900'
                            : 'text-gray-400'
                        }
                      >
                        {row.overrideCount}
                      </span>
                    </TableCell>
                  </TableRow>
                );
              })}
              {visibleRows.length === 0 && (
                <TableRow>
                  <TableCell
                    colSpan={3}
                    className="text-muted-foreground !py-8 text-center text-sm"
                  >
                    No flags match &ldquo;{filter}&rdquo;.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Card>
      </div>
    </TooltipProvider>
  );
}
